import React from "react";
import {
    IconButton,
    InputAdornment,
    InputBase,
    InputBaseProps,
    styled,
} from "@mui/material";
import { Search, X } from "lucide-react";

interface SearchBarProps extends Omit<InputBaseProps, "onChange"> {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    onClear?: () => void;
}

const StyledSearchBar = styled(InputBase)(({ theme }) => ({
    flex: 1,
    minWidth: "200px",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "6px",
    padding: "0.25rem 0.5rem 0.25rem 0.75rem",
    fontSize: "0.8rem",
    color: theme.palette.text.primary,
    height: "32px",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
        borderColor: theme.palette.grey[400],
    },
    "&.Mui-focused": {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}14`,
    },
    "& input": {
        padding: 0,
        height: "100%",
        fontWeight: 500,
        "&::placeholder": {
            color: theme.palette.text.disabled,
            opacity: 1,
            fontWeight: 400,
        },
    },
}));

const SearchBar: React.FC<SearchBarProps> = ({
    value,
    onChange,
    onClear,
    placeholder = "Search...",
    ...props
}) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
    };

    const showClear = onClear && value.length > 0;

    return (
        <StyledSearchBar
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            inputProps={{ "aria-label": "search" }}
            startAdornment={
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <Search size={14} style={{ opacity: 0.4, flexShrink: 0 }} />
                </InputAdornment>
            }
            endAdornment={
                showClear ? (
                    <InputAdornment position="end">
                        <IconButton
                            size="small"
                            onClick={onClear}
                            aria-label="Clear search"
                            sx={{
                                p: 0.25,
                                color: "text.secondary",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <X size={14} />
                        </IconButton>
                    </InputAdornment>
                ) : undefined
            }
            {...props}
        />
    );
};

export default SearchBar;
